import OrganizationDocumentUpload from '@/shared/components/OrganizationDocumentUpload'
import useI18n from '@/shared/hooks/usei18n'
import { SupplierEmissionsReportFormData } from './SupplierEmissionsReportForm.schema'
import {
  SupplierEmissionsReport,
  SupplierEmissionsReportEvidenceType
} from '@/features/suppliers/hooks/supplierEmissionsReport'
import { ListboxGroup, PageSection } from '@/common'
import { useMemo, useState } from 'react'

export function SupplierEmissionsReportEvidenceForm({
  data,
  onChange,
  supplierEmissionsReports
}: {
  data: SupplierEmissionsReportFormData
  onChange: (data: SupplierEmissionsReportFormData) => void
  supplierEmissionsReports: SupplierEmissionsReport[]
}) {
  const { t } = useI18n()

  const [isUploadingEvidenceEnabled, setIsUploadingEvidenceEnabled] = useState(
    data.evidenceType !== ''
  )

  const evidenceTypeOptions = Object.values(SupplierEmissionsReportEvidenceType).map(type => ({
    label: t(`supplierEmissionsReport:evidenceTypeOptions.${type}`),
    value: type
  }))

  // Get unique evidence file names from existing reports
  const existingEvidenceFiles = useMemo(() => {
    const evidenceFiles = supplierEmissionsReports
      .map(report => {
        return {
          evidenceFileName: report.evidenceFileName,
          evidenceType: report.evidenceType
        }
      })
      .filter(
        (
          evidenceFile
        ): evidenceFile is {
          evidenceFileName: string
          evidenceType: SupplierEmissionsReportEvidenceType
        } => Boolean(evidenceFile.evidenceFileName) && Boolean(evidenceFile.evidenceType)
      )

    // Remove duplicates based on evidenceFileName and evidenceType
    const uniqueFiles = evidenceFiles.filter(
      (evidenceFile, index, array) =>
        array.findIndex(
          file =>
            file.evidenceFileName === evidenceFile.evidenceFileName &&
            file.evidenceType === evidenceFile.evidenceType
        ) === index
    )

    return uniqueFiles.map(file => ({
      fileName: file.evidenceFileName,
      evidenceType: file.evidenceType,
      label: `${file.evidenceFileName} (${t(`supplierEmissionsReport:evidenceTypeOptions.${file.evidenceType}`)})`,
      value: `${file.evidenceFileName}-${file.evidenceType}`
    }))
  }, [supplierEmissionsReports, t])

  const handleEvidenceSelection = (value: string) => {
    const evidenceFile = existingEvidenceFiles.find(file => file.value === value)

    if (!evidenceFile) {return}

    onChange({
      ...data,
      evidenceFileName: evidenceFile.fileName,
      evidenceType: evidenceFile.evidenceType
    })
  }

  const handleEvidenceTypeSelection = (value: SupplierEmissionsReportEvidenceType | '') => {
    setIsUploadingEvidenceEnabled(value !== '')
    onChange({ ...data, evidenceType: value })
  }

  return (
    <PageSection
      title={t('supplierEmissionsReport:createSupplierEmissionsReportDialog.sections.evidence')}
    >
      <div className="space-y-4">
        <ListboxGroup
          label={t(
            'supplierEmissionsReport:supplierEmissionsReportInputFields.selectExistingEvidence'
          )}
          options={existingEvidenceFiles}
          value={`${data.evidenceFileName}-${data.evidenceType}`}
          onChange={handleEvidenceSelection}
          placeholder={t(
            'supplierEmissionsReport:supplierEmissionsReportInputFields.selectExistingEvidencePlaceholder'
          )}
        />

        <div className="text-sm text-gray-600">
          {t('supplierEmissionsReport:supplierEmissionsReportInputFields.orUploadNewEvidence')}
        </div>

        <div className="flex gap-2 items-end">
          <div className="w-2/5">
            <ListboxGroup<SupplierEmissionsReportEvidenceType>
              label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.evidenceType')}
              options={evidenceTypeOptions}
              value={data.evidenceType}
              onChange={handleEvidenceTypeSelection}
            />
          </div>
          <OrganizationDocumentUpload
            label={t('supplierEmissionsReport:supplierEmissionsReportInputFields.uploadEvidence')}
            fileName={data.evidenceFileName}
            disabled={!isUploadingEvidenceEnabled}
            onDocumentUploaded={value => onChange({ ...data, evidenceFileName: value })}
            onDocumentDeleted={() => onChange({ ...data, evidenceFileName: '' })}
          />
        </div>
      </div>
    </PageSection>
  )
}
